/**
 * useBrainPageVisits — records and reads Brain/Playground page visits.
 *
 * Stores the set of pathnames the user has *actually opened* in the
 * current browser, in localStorage. The `WorkflowJourney` strip and any
 * future "you have N more pages to explore" cue read from this.
 *
 * Why localStorage and not server-side: this is a UX progress nudge,
 * not an audit trail. We don't need cross-device sync; we *do* need
 * fast reads with no API hop and no auth dependency.
 */
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

const STORAGE_KEY = 'brain.page.visits'

function loadVisited(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const arr = raw ? (JSON.parse(raw) as string[]) : []
    return new Set(arr)
  } catch {
    return new Set()
  }
}

function persist(visited: Set<string>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...visited]))
  } catch {
    /* localStorage may be disabled — fail open */
  }
}

export function useBrainPageVisits() {
  const { pathname } = useLocation()
  const [visited, setVisited] = useState<Set<string>>(() => loadVisited())

  useEffect(() => {
    const isTracked =
      pathname.startsWith('/brain') || pathname.startsWith('/playground')
    if (!isTracked) return
    setVisited((prev) => {
      if (prev.has(pathname)) return prev
      const next = new Set(prev)
      next.add(pathname)
      persist(next)
      return next
    })
  }, [pathname])

  const reset = useCallback(() => {
    setVisited(new Set())
    persist(new Set())
  }, [])

  return { visited, count: visited.size, reset }
}
